import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Button, Col, DatePicker, Descriptions, Form, Input, InputNumber, Result, Row, Select, Space, Spin, message } from 'antd'
import { FilePdfOutlined } from '@ant-design/icons'

import Attachments from './Attachments'
import BlobUploader from './BlobUploader'
import { openLoanAgreementPdf } from './openPdf'
import {
  DEFAULT_WITH_HOLDING_TAX_RATE,
  DELETE_LOAN_AGREEMENT_ATTACHMENT_MUTATION,
  INTEREST_STATUSES,
  LOAN_AGREEMENT_QUERY,
  PRINCIPLE_STATUSES,
  UPSERT_LOAN_AGREEMENT_MUTATION,
  formatMutationErrors
} from './graphql'

const DATE_FORMAT = 'YYYY-MM-DD'
const DISPLAY_FORMAT = 'DD/MM/YYYY'

const NEW_VALUES = {
  withHoldingTaxRate: DEFAULT_WITH_HOLDING_TAX_RATE,
  principleStatus: 'principle_pending',
  interestStatus: 'interest_pending',
  signedDocumentBlobIds: [],
  slipBlobIds: []
}

const toDay = value => (value ? dayjs(value) : null)

const toDate = value => (value ? value.format(DATE_FORMAT) : null)

const toNumber = value => (value === undefined || value === null || value === '' ? null : Number(value))

const toFormValues = loanAgreement => ({
  borrower: loanAgreement.borrower,
  issueDate: toDay(loanAgreement.issueDate),
  amount: loanAgreement.amount ? loanAgreement.amount.value : null,
  interestPeriod: loanAgreement.interestPeriod,
  interestRate: loanAgreement.interestRate,
  withHoldingTaxRate: loanAgreement.withHoldingTaxRate,
  principleStatus: loanAgreement.principleStatus,
  interestStatus: loanAgreement.interestStatus,
  principlePaidDate: toDay(loanAgreement.principlePaidDate),
  interestPaidDate: toDay(loanAgreement.interestPaidDate),
  paymentNote: loanAgreement.paymentNote,
  signedDocumentBlobIds: [],
  slipBlobIds: []
})

const toVariables = (id, values) => ({
  id: id || null,
  borrower: values.borrower || null,
  issueDate: toDate(values.issueDate),
  amount: toNumber(values.amount),
  interestPeriod: toNumber(values.interestPeriod),
  interestRate: toNumber(values.interestRate),
  withHoldingTaxRate: toNumber(values.withHoldingTaxRate),
  principleStatus: values.principleStatus,
  interestStatus: values.interestStatus,
  principlePaidDate: toDate(values.principlePaidDate),
  interestPaidDate: toDate(values.interestPaidDate),
  paymentNote: values.paymentNote || null,
  signedDocumentBlobIds: values.signedDocumentBlobIds || [],
  slipBlobIds: values.slipBlobIds || []
})

const Computed = ({ loanAgreement }) => {
  const { t } = useTranslation('loanAgreements')
  const money = value => (value ? value.display : '-')

  return (
    <Descriptions bordered column={2} size='small' style={{ marginBottom: 16 }}>
      <Descriptions.Item label={t('dueDate')}>
        {loanAgreement.dueDate ? dayjs(loanAgreement.dueDate).format(DISPLAY_FORMAT) : '-'}
      </Descriptions.Item>
      <Descriptions.Item label={t('interestPerDay')}>{money(loanAgreement.interestPerDay)}</Descriptions.Item>
      <Descriptions.Item label={t('totalInterest')}>{money(loanAgreement.totalInterest)}</Descriptions.Item>
      <Descriptions.Item label={t('withHoldingTax')}>{money(loanAgreement.withHoldingTax)}</Descriptions.Item>
      <Descriptions.Item label={t('totalAmount')} span={2}>{money(loanAgreement.totalAmount)}</Descriptions.Item>
    </Descriptions>
  )
}

Computed.propTypes = {
  loanAgreement: PropTypes.object.isRequired
}

const LoanAgreementForm = ({ id, onClose, onSaved }) => {
  const { t } = useTranslation('loanAgreements')
  const isEdit = Boolean(id)
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState({ signedDocuments: false, slips: false })
  const [deletingId, setDeletingId] = useState(null)
  const [pdfLoading, setPdfLoading] = useState(false)

  const { data, loading } = useQuery(LOAN_AGREEMENT_QUERY, {
    skip: !isEdit,
    variables: { id },
    fetchPolicy: 'network-only'
  })

  const [upsertLoanAgreement] = useMutation(UPSERT_LOAN_AGREEMENT_MUTATION)
  const [deleteAttachment] = useMutation(DELETE_LOAN_AGREEMENT_ATTACHMENT_MUTATION)

  const loanAgreement = data && data.loanAgreement
  const isUploading = uploading.signedDocuments || uploading.slips

  useEffect(() => {
    if (loanAgreement) form.setFieldsValue(toFormValues(loanAgreement))
  }, [loanAgreement && loanAgreement.id])

  const setUploadingFor = name => busy => setUploading(current => ({ ...current, [name]: busy }))

  const handleSubmit = async (values) => {
    setSaving(true)
    try {
      const { data: response } = await upsertLoanAgreement({ variables: toVariables(id, values) })
      const payload = response && response.upsertLoanAgreement
      if (!payload) { message.error(t('saveFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t('saveSuccess'))
      onSaved(payload.loanAgreement)
    } catch (error) {
      message.error(error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteAttachment = async (attachment) => {
    setDeletingId(attachment.id)
    try {
      await deleteAttachment({ variables: { id, attachmentId: attachment.id } })
      message.success(t('deleteSuccess'))
    } catch (error) {
      message.error(error.message)
    } finally {
      setDeletingId(null)
    }
  }

  const handleDownload = async () => {
    setPdfLoading(true)
    const opened = await openLoanAgreementPdf(id)
    setPdfLoading(false)
    if (!opened) message.error(t('downloadFailed'))
  }

  if (isEdit && loading && !loanAgreement) return <Spin />

  if (isEdit && !loanAgreement) {
    return <Result extra={<Button onClick={onClose}>{t('close')}</Button>} status='404' title={t('notFound')} />
  }

  const statusOptions = statuses => statuses.map(value => (
    <Select.Option key={value} value={value}>{t(`statuses.${value}`)}</Select.Option>
  ))

  return (
    <>
      {isEdit && <Computed loanAgreement={loanAgreement} />}

      <Form
        disabled={saving}
        form={form}
        initialValues={NEW_VALUES}
        layout='vertical'
        onFinish={handleSubmit}
      >
        <Form.Item label={t('borrower')} name='borrower'>
          <Input />
        </Form.Item>

        <Row gutter={16}>
          <Col sm={12} xs={24}>
            <Form.Item label={t('issueDate')} name='issueDate'>
              <DatePicker format={DISPLAY_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item label={t('amount')} name='amount'>
              <InputNumber min={0} precision={2} step={1000} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={8} xs={24}>
            <Form.Item label={t('interestPeriodDays')} name='interestPeriod'>
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={8} xs={24}>
            <Form.Item label={t('interestRate')} name='interestRate'>
              <InputNumber min={0} step={0.25} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={8} xs={24}>
            <Form.Item extra={t('withHoldingTaxRateHint')} label={t('withHoldingTaxRate')} name='withHoldingTaxRate'>
              <InputNumber max={1} min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item label={t('principleStatus')} name='principleStatus'>
              <Select>{statusOptions(PRINCIPLE_STATUSES)}</Select>
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item label={t('principlePaidDate')} name='principlePaidDate'>
              <DatePicker format={DISPLAY_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item label={t('interestStatus')} name='interestStatus'>
              <Select>{statusOptions(INTEREST_STATUSES)}</Select>
            </Form.Item>
          </Col>
          <Col sm={12} xs={24}>
            <Form.Item label={t('interestPaidDate')} name='interestPaidDate'>
              <DatePicker format={DISPLAY_FORMAT} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label={t('paymentNote')} name='paymentNote'>
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item label={t('signedDocuments')}>
          {isEdit && (
            <div style={{ marginBottom: 8 }}>
              <Attachments
                attachments={loanAgreement.signedDocuments}
                deletingId={deletingId}
                onDelete={handleDeleteAttachment}
              />
            </div>
          )}
          <Form.Item name='signedDocumentBlobIds' noStyle>
            <BlobUploader onBusyChange={setUploadingFor('signedDocuments')} />
          </Form.Item>
        </Form.Item>

        <Form.Item label={t('slips')}>
          {isEdit && (
            <div style={{ marginBottom: 8 }}>
              <Attachments
                attachments={loanAgreement.slips}
                deletingId={deletingId}
                onDelete={handleDeleteAttachment}
              />
            </div>
          )}
          <Form.Item name='slipBlobIds' noStyle>
            <BlobUploader onBusyChange={setUploadingFor('slips')} />
          </Form.Item>
        </Form.Item>

        <Space wrap>
          <Button disabled={isUploading} htmlType='submit' loading={saving} type='primary'>
            {isEdit ? t('save') : t('create')}
          </Button>
          <Button onClick={onClose}>{t('cancel')}</Button>
          {isEdit && (
            <Button icon={<FilePdfOutlined />} loading={pdfLoading} onClick={handleDownload}>
              {t('downloadPdf')}
            </Button>
          )}
        </Space>
      </Form>
    </>
  )
}

LoanAgreementForm.propTypes = {
  id: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
}

LoanAgreementForm.defaultProps = {
  id: null
}

export default LoanAgreementForm
